
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { deleteInternalMessage } from "@/services/internalMessageService";

export interface Alert {
  id: number;
  type: "warning" | "danger" | "info";
  message: string;
  date: string;
  status: "open" | "resolved" | "pending";
  source?: "internal_message" | "time_entry" | "absence";
  sourceId?: string;
}

export const useAlertsData = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      console.log("🔍 [AlertsData] Début de la récupération des alertes");
      
      const generatedAlerts: Alert[] = [];
      let alertId = 1;

      // 1. Récupérer les messages internes pour les afficher comme alertes
      console.log("📨 [AlertsData] Récupération des messages internes...");
      const { data: internalMessages, error: messagesError } = await supabase
        .from('internal_messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (!messagesError && internalMessages) {
        console.log(`📨 [AlertsData] ${internalMessages.length} messages internes trouvés`);
        
        internalMessages.forEach(message => {
          const alertType = message.priority === 'urgent' ? 'danger' : 
                          message.priority === 'high' ? 'warning' : 'info';
          
          generatedAlerts.push({
            id: alertId++,
            type: alertType,
            message: `Signalement: ${message.subject} - De: ${message.sender_name}`,
            date: message.created_at,
            status: message.status === 'resolved' ? 'resolved' : 
                   message.status === 'in_progress' ? 'pending' : 'open',
            source: "internal_message",
            sourceId: message.id
          });
        });
      } else if (messagesError) {
        console.error("❌ [AlertsData] Erreur messages internes:", messagesError);
      }

      // 2. Récupérer les entrées de temps pour détecter les anomalies
      console.log("⏰ [AlertsData] Récupération des entrées de temps...");
      const { data: timeEntries, error } = await supabase
        .from('time_entries')
        .select(`
          *,
          profiles!inner(name),
          worksites!inner(name)
        `)
        .gte('date', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);

      if (error) {
        console.error("❌ [AlertsData] Erreur time_entries:", error);
        throw error;
      }

      console.log(`⏰ [AlertsData] ${timeEntries?.length || 0} entrées de temps trouvées`);

      // 3. Analyser les entrées pour générer des alertes
      timeEntries?.forEach(entry => {
        if (entry.start_time && entry.end_time) {
          const startTime = new Date(`1970-01-01T${entry.start_time}`);
          const endTime = new Date(`1970-01-01T${entry.end_time}`);
          const heures = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

          // Alerte pour plus de 10h de travail
          if (heures > 10) {
            generatedAlerts.push({
              id: alertId++,
              type: "warning",
              message: `${entry.profiles.name} a travaillé ${Math.round(heures * 10) / 10}h le ${new Date(entry.date).toLocaleDateString()}`,
              date: entry.date,
              status: "open",
              source: "time_entry",
              sourceId: entry.id
            });
          }
        }

        // Alerte pour entrée sans sortie
        if (entry.start_time && !entry.end_time) {
          generatedAlerts.push({
            id: alertId++,
            type: "danger",
            message: `Pointage de sortie manquant pour ${entry.profiles.name} le ${new Date(entry.date).toLocaleDateString()}`,
            date: entry.date,
            status: "open",
            source: "time_entry",
            sourceId: entry.id
          });
        }
      });

      // 4. Vérifier les absences (profils actifs sans entrées récentes)
      console.log("👥 [AlertsData] Vérification des absences...");
      const { data: activeProfiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name')
        .eq('active', true)
        .eq('role', 'ouvrier');

      if (!profilesError && activeProfiles) {
        const workersWithEntries = new Set(timeEntries?.map(entry => entry.profiles.name) || []);
        
        activeProfiles.forEach(profile => {
          if (!workersWithEntries.has(profile.name)) {
            generatedAlerts.push({
              id: alertId++,
              type: "info",
              message: `Aucune activité récente pour ${profile.name}`,
              date: new Date().toISOString().split('T')[0],
              status: "pending",
              source: "absence",
              sourceId: profile.id
            });
          }
        });
      }

      console.log(`✅ [AlertsData] ${generatedAlerts.length} alertes générées au total`);
      setAlerts(generatedAlerts);

    } catch (error) {
      console.error('❌ [AlertsData] Erreur lors du chargement des alertes:', error);
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  const deleteAlert = async (alertId: number) => {
    const alert = alerts.find(a => a.id === alertId);
    if (!alert) {
      toast.error('Alerte non trouvée');
      return;
    }

    try {
      // Supprimer réellement selon la source
      if (alert.source === "internal_message" && alert.sourceId) {
        const success = await deleteInternalMessage(alert.sourceId);
        if (!success) {
          toast.error('Erreur lors de la suppression du message source');
          return;
        }
      } else if (alert.source === "time_entry" && alert.sourceId) {
        // Pour les entrées de temps, on peut les marquer comme résolues ou les corriger
        // Ici on va juste les supprimer de l'affichage local pour l'instant
        toast.info('Alerte d\'entrée de temps marquée comme résolue');
      } else if (alert.source === "absence") {
        // Pour les absences, on supprime juste de l'affichage
        toast.info('Alerte d\'absence supprimée');
      }

      // Supprimer de l'état local
      setAlerts(prevAlerts => prevAlerts.filter(alert => alert.id !== alertId));
      toast.success('Alerte supprimée');

      // Recharger pour s'assurer de la cohérence
      setTimeout(() => {
        fetchAlerts();
      }, 1000);

    } catch (error) {
      console.error('Erreur lors de la suppression de l\'alerte:', error);
      toast.error('Erreur lors de la suppression de l\'alerte');
    }
  };

  return { alerts, setAlerts, loading, deleteAlert, refreshAlerts: fetchAlerts };
};
